import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, Search, Bookmark, BookmarkCheck, Heart } from "lucide-react";
import { setPendingSave } from "@/lib/pendingSave";

const TYPES = [
  { id: "ALL",         label: "Все" },
  { id: "ARTICLE",     label: "Статьи" },
  { id: "RECIPE",      label: "Рецепты" },
  { id: "FORMULATION", label: "Рецептуры" },
];

const TYPE_LABEL = {
  ARTICLE: "Статьи",
  RECIPE: "Рецепты",
  FORMULATION: "Рецептуры",
  GUIDE: "Гайд",
  REFERENCE: "Справочник",
};

export default function Library() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [likedIds, setLikedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("search", q.trim());
      if (type !== "ALL") params.set("material_type", type);
      const { data } = await api.get(`/library/materials${params.toString() ? `?${params}` : ""}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, type]);

  const loadSaved = useCallback(async () => {
    if (!user) { setSavedIds(new Set()); setLikedIds(new Set()); return; }
    try {
      const [s, l] = await Promise.all([api.get("/account/saved"), api.get("/account/likes/ids")]);
      setSavedIds(new Set(s.data.map((m) => m.id)));
      setLikedIds(new Set(l.data));
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const toggleLike = async (material) => {
    if (!user) {
      toast("Войдите, чтобы отметить понравившееся");
      nav("/login");
      return;
    }
    const isLiked = likedIds.has(material.id);
    try {
      if (isLiked) {
        await api.delete(`/account/likes/${material.id}`);
        const next = new Set(likedIds); next.delete(material.id); setLikedIds(next);
      } else {
        await api.post("/account/likes", { material_id: material.id });
        const next = new Set(likedIds); next.add(material.id); setLikedIds(next);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSave = async (material) => {
    if (!user) {
      setPendingSave(material.id);
      toast("Войдите, чтобы сохранить материал", {
        description: "После входа мы автоматически добавим его в «Сохранённое».",
      });
      nav("/login");
      return;
    }
    const isSaved = savedIds.has(material.id);
    try {
      if (isSaved) {
        await api.delete(`/account/saved/${material.id}`);
        const next = new Set(savedIds); next.delete(material.id); setSavedIds(next);
        toast.success("Удалено из «Сохранённое»");
      } else {
        await api.post("/account/saved", { material_id: material.id });
        const next = new Set(savedIds); next.add(material.id); setSavedIds(next);
        toast.success("Сохранено в «Сохранённое»");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="library-page">
      <header className="max-w-5xl mx-auto px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          onClick={() => nav("/hall")}
          data-testid="back-to-hall-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"
          aria-label="В главный зал"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Anokina Studio · Open Library
          </div>
          <h1 className="font-serif text-3xl text-espresso -mt-0.5" data-testid="library-title">
            Открытая библиотека
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Рецепты, статьи, разборы и справочные материалы Anokina Studio.
        </p>

        <div className="relative mt-5">
          <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="library-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по названию или описанию"
            className="pl-10 h-11 rounded-full bg-white"
          />
        </div>

        <div className="overflow-x-auto -mx-1 px-1 mt-4">
          <div className="flex gap-2 w-max" data-testid="library-filters">
            {TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setType(t.id)}
                data-testid={`filter-${t.id}`}
                className={`chip whitespace-nowrap px-4 py-1.5 text-sm border transition-colors ${
                  type === t.id
                    ? "bg-espresso text-white border-espresso"
                    : "bg-white text-espresso border-[#E1D2B8] hover:bg-sand/40"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 pb-10">
          {loading ? (
            <LoadingGrid />
          ) : items.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-list">
              {items.map((m) => (
                <MaterialCard
                  key={m.id}
                  material={m}
                  saved={savedIds.has(m.id)}
                  liked={likedIds.has(m.id)}
                  onToggleSave={() => toggleSave(m)}
                  onToggleLike={() => toggleLike(m)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function MaterialCard({ material, saved, liked, onToggleSave, onToggleLike }) {
  const nav = useNavigate();
  const open = () => nav(`/library/${material.slug || material.id}`);
  return (
    <li
      className="rounded-3xl bg-white border hairline overflow-hidden flex flex-col hover:shadow-md transition-shadow"
      data-testid={`library-card-${material.id}`}
    >
      <button onClick={open} className="text-left" aria-label={`Открыть материал ${material.title}`}>
        <div className="aspect-[4/5] bg-sand/40 relative overflow-hidden">
          {material.cover_image ? (
            <img
              src={material.cover_image}
              alt={material.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-3xl text-espresso/40 select-none">
              A
            </div>
          )}
        </div>
      </button>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <span data-testid={`card-type-${material.id}`}>{TYPE_LABEL[material.material_type] || material.material_type}</span>
        </div>
        <button onClick={open} className="text-left">
          <h3 className="font-serif text-lg text-espresso leading-snug hover:text-espressoDark">
            {material.title}
          </h3>
        </button>
        {material.short_description && (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {material.short_description}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-2">
          <Link
            to={`/library/${material.slug || material.id}`}
            className="text-xs uppercase tracking-widest text-espresso hover:text-espressoDark"
            data-testid={`card-open-${material.id}`}
          >
            Читать →
          </Link>
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleLike}
              data-testid={`card-like-${material.id}`}
              aria-pressed={liked}
              className={`p-2 rounded-full transition-colors ${liked ? "bg-red-50 text-red-500" : "hover:bg-sand/50 text-espresso"}`}
              title={liked ? "Убрать из понравившихся" : "Нравится"}
            >
              <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} />
            </button>
            <button
              onClick={onToggleSave}
              data-testid={`card-save-${material.id}`}
              aria-pressed={saved}
              className={`p-2 rounded-full transition-colors ${
                saved ? "bg-espresso text-white" : "hover:bg-sand/50 text-espresso"
              }`}
              title={saved ? "Убрать из сохранённого" : "Сохранить"}
            >
              {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

function LoadingGrid() {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-loading">
      {[0, 1, 2].map((i) => (
        <li key={i} className="rounded-3xl bg-white border hairline overflow-hidden">
          <div className="h-40 bg-sand/30 animate-pulse" />
          <div className="p-4 space-y-2">
            <div className="h-3 w-24 bg-sand/40 rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-sand/40 rounded animate-pulse" />
            <div className="h-3 w-full bg-sand/40 rounded animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16" data-testid="library-empty">
      <p className="font-serif text-xl text-espresso">Пока пусто</p>
      <p className="text-sm text-muted-foreground mt-2">
        Материалы появятся, как только редакция опубликует их.
      </p>
    </div>
  );
}
